import { useUserStore } from '@Auth/store';
import { selectAssignableTeams } from '@helpers/teams';
import type { GetTeamsResponse } from '@responses/teams';
import type { QueryClient } from '@tanstack/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { getRequestAuthPayload } from '../helpers/getRequestAuth';

import type { QueryFactory } from './factory.types';

const fetchTeams = async (shouldShowAllTeams = false): Promise<GetTeamsResponse> => {
  const params = shouldShowAllTeams === true ? { type: 'template' } : null;
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/teams',
    params,
  });

  const response = await axios(networkPayload);
  return response.data;
};

export const fetchAllTeams = async () => await fetchTeams(true);
export const fetchUserTeams = async () => await fetchTeams(false);

export const teamQueries = {
  list: () =>
    queryOptions({
      queryKey: ['allTeams'] as const,
      queryFn: fetchAllTeams,
    }),
  forUser: (tenantIds: number[] = []) =>
    queryOptions({
      queryKey: ['userTeams', ...tenantIds] as const,
      queryFn: fetchUserTeams,
    }),
} satisfies QueryFactory;

export const invalidateTeams = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: teamQueries.list().queryKey }),
    queryClient.invalidateQueries({ queryKey: teamQueries.forUser().queryKey }),
  ]);

export function useAllTeams() {
  return useQuery(teamQueries.list());
}

export function useAssignableTeams() {
  return useQuery({ ...teamQueries.list(), select: (teams) => selectAssignableTeams(teams) });
}

export function useTeamsForUser() {
  const tenantIds = useUserStore((s) => s.user?.tenantIds ?? []);
  return useQuery(teamQueries.forUser(tenantIds));
}
